<script lang="ts" setup>
import { computed, PropType, ref } from "vue";
import {
  PopoverPositionEnum,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmPopover,
  TirPmSpinner,
} from "tir-components";
import { FileManager } from "tir-pm-file-upload-manager";
import { ComponentInstance } from "tir-pm-toolkit";
import { CrossLinedIcon } from "tir-style-system/icons/outline";
import { TaskFileModel } from "../../models";

const props = defineProps({
  /**Список файлов в задаче */
  files: {
    type: Array as PropType<Array<TaskFileModel>>,
    required: true,
  },
  /**Загрузка */
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  /**Отменить загрузку */
  cancelUpload: [files: Array<TaskFileModel>];
}>();

/**Ссылка на компонент поповера */
const $popover = ref<ComponentInstance<typeof TirPmPopover>>();
/**Загружаемые файлы */
const uploadingFiles = computed(() =>
  props.files.filter((file) => file.UploadProgress !== undefined && file.UploadProgress !== 100),
);
/**Текст прогресса загрузки */
const progressText = computed(
  () =>
    `${!uploadingFiles.value.length ? "Загружено" : "Загружается"}: ${
      props.files.length - uploadingFiles.value.length
    }/${props.files.length}`,
);

/**Отключить кнопку отмены */
const isDisabledCancelButton = computed(() =>
  uploadingFiles.value.some(
    (file) =>
      !FileManager.ActiveUploads.value.some((_file) => _file.FileId === file.Id) && !file.UploadId,
  ),
);

/**Прервать загрузку файлов */
const cancelImport = async () => {
  if (props.isLoading) return;

  emit("cancelUpload", uploadingFiles.value);
};
</script>

<template>
  <div class="task-files-progress">
    <span class="task-files-progress__label">Прикрепленные файлы</span>
    <div class="task-files-progress__main">
      <TirPmSpinner
        v-if="uploadingFiles.length"
        class="task-files-progress__spinner"
        :size="'small'"
      />
      <span class="task-files-progress__text">{{ progressText }}</span>
      <div class="task-files-progress__cancel-button" v-if="uploadingFiles.length">
        <TirPmPopover ref="$popover" :position="PopoverPositionEnum.Bottom">
          <TirPmButton
            :variant="TirPmButtonVariantEnum.SecondaryState"
            :size="TirPmButtonSizeEnum.Small"
          >
            <template #leftSlot>
              <CrossLinedIcon />
            </template>
          </TirPmButton>
          <template #content>
            <div class="task-files-progress__cancel-popover-content">
              <span class="task-files-progress__cancel-popover-text">Прервать загрузку?</span>
              <div class="task-files-progress__cancel-popover-buttons">
                <TirPmButton
                  :variant="TirPmButtonVariantEnum.PrimaryState"
                  :size="TirPmButtonSizeEnum.Small"
                  :is-loading="isLoading"
                  :is-disabled="isDisabledCancelButton"
                  @click="cancelImport"
                >
                  Да
                </TirPmButton>
                <TirPmButton
                  :variant="TirPmButtonVariantEnum.SecondaryState"
                  :size="TirPmButtonSizeEnum.Small"
                  @click="$popover?.close"
                >
                  Нет
                </TirPmButton>
              </div>
            </div>
          </template>
        </TirPmPopover>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.task-files-progress {
  display: flex;
  flex-direction: column;

  &__label {
    color: var(--text-tertiary, #93969b);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
  }

  &__main {
    display: flex;
    align-items: center;
  }
  &__spinner {
    margin-left: 0.75rem;
  }
  &__text {
    margin-left: 0.375rem;
  }
  &__cancel-button {
    margin-left: 1rem;
  }
  &__cancel-popover-content {
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  &__cancel-popover-text {
    color: var(--text-primary, #272d37);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
  }
  &__cancel-popover-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
